import { useEffect, useState, useMemo, useRef, isValidElement } from 'react';
import { App, Layout, Tabs, Image, Form, Button, Modal, Input, Radio, message, Table, Typography, Space, Tooltip, Alert, Row, Col, Checkbox, Tag, Switch } from 'antd';
// import { LeftOutlined, SettingOutlined } from '@ant-design/icons';
import { useParams, Link, useNavigate } from 'react-router-dom';
import './CaseDetail.less';
import { caseBaseInfoApi, caseDetailApi, caseFileApiApi, caseTeamApi, caseOtherFileApi, caseContactInfoApi, getVodMediaInfoApi, caseAuditApi, getDicNameApi, getProjectTopicFieldsApi, caseFileReviewedApi, caseFileShowedChangeApi, getPeopleInfoApi, getProjestsApi } from '@/services/Selection';
import { formatAiReviewRejectReasons, isProjectAiReviewEnabled, parseAiReviewMessageParts, hasAiReviewMessageContent, AI_REVIEW_MSG_KEYS } from '../aiReviewUtils';
import PageHeader from '@components/PageHeader/PageHeader';
import { identityTypes, orgAuthTypes } from '@/utils/constants';
import { prepareBCOSSUrl, areasString, dateTimeFormatHMS, getAdminInfo } from '@utils/utils';
import { isEmpty } from 'lodash';
import CasePoster from './CasePoster';
import { buildRejectLetter, DEFAULT_SETTINGS, loadSettings, saveSettings, PRESET_REASONS, isDeadlineReady, validatePresetExtras, hasRejectReasonSelection } from './caseAuditRejectSettings';
const { TextArea } = Input;
const { Text } = Typography;

// 视频点播
let player = null;

let allDicCodeList = [];

/** 字典码转名称：兼容 string（如 post）与 Cascader 路径数组（如 department/title） */
const resolveDicName = (dicCodeList, dicCode) => {
    if (!dicCode || !dicCodeList?.length) return '';
    const codes = Array.isArray(dicCode) ? dicCode : [dicCode];
    if (!codes.length) return '';
    const names = codes.map(code => dicCodeList.find(item => item.dicCode === code)?.dicName).filter(Boolean);
    return names.length ? names.join('/') : '';
};

/** 理由正文：收起时单行省略，超出时显示展开 */
function ReasonExpandableText({ text }) {
    const full = String(text ?? '');
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
        setExpanded(false);
    }, [full]);

    useEffect(() => {
        if (!full || expanded) {
            return;
        }
        const el = textRef.current;
        if (!el) {
            return;
        }
        const measure = () => {
            setOverflows(el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1);
        };
        measure();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        ro?.observe(el);
        return () => ro?.disconnect();
    }, [full, expanded]);

    if (!full) {
        return null;
    }

    const showToggle = expanded || overflows;

    return (
        <div className={`case-reason-card-body${expanded ? ' case-reason-card-body--expanded' : ''}`}>
            <span ref={textRef} className={`case-reason-card-text${expanded ? '' : ' case-reason-card-text--clamp'}`}>
                {full}
            </span>
            {showToggle ? (
                <Button type="link" size="small" className="case-reason-card-toggle" onClick={() => setExpanded(v => !v)}>
                    {expanded ? '收起' : '展开'}
                </Button>
            ) : null}
        </div>
    );
}

// 基础信息
const BaseInfo = ({ caseFields, getFieldName, getSecondField }) => {
    const params = useParams();

    const [baseInfo, setBaseInfo] = useState({});
    const [baseFieldInfo, setBaseFieldInfo] = useState({});
    const [fields, setFields] = useState([]);

    const [dicCodeList, setDicCodeList] = useState([]);
    const [personDicList, setPersonDicList] = useState(null);

    const getCaseInfo = async caseId => {
        const res = await caseBaseInfoApi({ id: caseId });
        if (res.status_code === 200 && res.data) {
            setBaseInfo(res.data);
            const field = res.data.fieldValue ? JSON.parse(res.data.fieldValue) : null;
            if (field) {
                setBaseFieldInfo(field);
            }
            if (field.person_case_info) {
                setPersonDicList(field.person_case_info);
            }
        }
    };

    // 获取字典名称
    const getDicName = dicCode => resolveDicName(dicCodeList, dicCode);

    // 获取职务职称字段
    const getCodeName = async () => {
        const res = await getDicNameApi({ identity: 'A01' });
        if (res.status_code === 200 && res.data) {
            setDicCodeList(res.data);
            allDicCodeList = res.data;
        }
    };

    // 获取地区字段
    const getAreaValue = areaCode => {
        let val = [];
        const areaList = Object.values(areasString);
        if (areaCode && areaCode.length) {
            val = areaCode.map((code, index) => {
                return areaList[index].find(it => it.value === code)?.label || '';
            });
        }
        if (val && val.length) {
            return val.join(' ');
        }
        return '';
    };

    useEffect(() => {
        getCodeName();
        getCaseInfo(params.id);
    }, []);

    useEffect(() => {
        document.title = '实践详情-医评议';
    }, []);

    useEffect(() => {
        if (caseFields) {
            const newCaseFields = caseFields.map(item => item.field_name);
            setFields(newCaseFields);
        }
    }, [caseFields]);

    return (
        <div className="base-info">
            <div className="base-cover-wrap">
                <div className="base-cover">
                    <Image src={prepareBCOSSUrl(baseFieldInfo?.cover)} alt="实践封面" />
                </div>
                <div className="cover-info">
                    <p>实践编号：{params.id}</p>
                    {fields.includes('case_org_id') && (
                        <p>
                            {getFieldName('case_org_id') || '申报单位'}：{baseInfo.hospitalName}
                        </p>
                    )}
                    {fields.includes('region') && (
                        <p>
                            {getFieldName('region') || '所属赛区'}：{baseFieldInfo?.region}
                        </p>
                    )}
                    {fields.includes('topic_id') && (
                        <p>
                            {getFieldName('topic_id') || '实践主题'}：{baseInfo.topicName}
                        </p>
                    )}
                    {fields.includes('direction_id') && (
                        <p>
                            {getFieldName('direction_id') || '主题方向'}：{baseInfo.directionName}
                        </p>
                    )}
                    {fields.includes('area') && (
                        <p>
                            {getFieldName('area') || '提报单位所在地'}：{getAreaValue(baseFieldInfo?.area)}
                        </p>
                    )}
                </div>
            </div>

            <Form layout="horizontal">
                {fields.includes('party_org_name') && (
                    <Form.Item label={`${getFieldName('party_org_name') || '党组织名称'}：`}>
                        <div>{baseFieldInfo?.party_org_name}</div>
                    </Form.Item>
                )}

                {fields.includes('party_org_basic_info') && (
                    <Form.Item label={`${getFieldName('party_org_basic_info') || '党组织基本情况'}：`}>
                        <div>{baseFieldInfo?.party_org_basic_info}</div>
                    </Form.Item>
                )}

                {fields.includes('party_org_innovation') && (
                    <Form.Item label={`${getFieldName('party_org_innovation') || '党建业务融合创新做法'}：`}>
                        <div>{baseFieldInfo?.party_org_innovation}</div>
                    </Form.Item>
                )}

                {fields.includes('work_effect') && (
                    <Form.Item label={`${getFieldName('work_effect') || '工作效果'}：`}>
                        <div>{baseFieldInfo?.work_effect}</div>
                    </Form.Item>
                )}

                {fields.includes('work_inspiration') && (
                    <Form.Item label={`${getFieldName('work_inspiration') || '工作启示'}：`}>
                        <div>{baseFieldInfo?.work_inspiration}</div>
                    </Form.Item>
                )}

                {fields.includes('introduction') && (
                    <Form.Item label={`${getFieldName('introduction') || '实践简介'}：`}>
                        <div>{baseFieldInfo?.introduction}</div>
                    </Form.Item>
                )}

                {fields.includes('person_case_info') && (
                    <Form.Item label={`${getFieldName('person_case_info') || '人物信息'}：`}>
                        {!!dicCodeList.length && personDicList && (
                            <div className="contact-process">
                                {getSecondField('person_case_info', 'department') && (
                                    <div>
                                        <span>科室/部门：</span>
                                        {getDicName(personDicList.department)}
                                    </div>
                                )}
                                {getSecondField('person_case_info', 'post') && (
                                    <div>
                                        <span>职务：</span>
                                        {getDicName(personDicList.post)}
                                    </div>
                                )}
                                {getSecondField('person_case_info', 'title') && (
                                    <div>
                                        <span>职称：</span>
                                        {getDicName(personDicList.title)}
                                    </div>
                                )}
                                {getSecondField('person_case_info', 'phone') && (
                                    <div>
                                        <span>手机：</span>
                                        {personDicList.phone}
                                    </div>
                                )}
                                {getSecondField('person_case_info', 'email') && (
                                    <div>
                                        <span>邮箱：</span>
                                        {personDicList.email}
                                    </div>
                                )}
                            </div>
                        )}
                    </Form.Item>
                )}

                {fields.includes('background') && (
                    <Form.Item label={`${getFieldName('background') || '实践背景'}：`}>
                        <div>{baseFieldInfo?.background}</div>
                    </Form.Item>
                )}

                {fields.includes('processes') && (
                    <Form.Item label={`${getFieldName('processes') || '流程结构'}：`}>
                        {baseFieldInfo?.processes
                            && baseFieldInfo.processes.length
                            && baseFieldInfo.processes.map((p, i) => (
                                <div className="base-process" key={i}>
                                    {p?.process}
                                </div>
                            ))}
                    </Form.Item>
                )}

                {fields.includes('features') && (
                    <Form.Item label={`${getFieldName('features') || '创新亮点'}：`}>
                        {baseFieldInfo?.features
                            && baseFieldInfo.features.length
                            && baseFieldInfo.features.map((f, i) => (
                                <div className="base-process" key={i}>
                                    {f?.feature}
                                </div>
                            ))}
                    </Form.Item>
                )}

                {fields.includes('scenarios') && (
                    <Form.Item label={`${getFieldName('scenarios') || '应用场景'}：`}>
                        {baseFieldInfo?.scenarios
                            && baseFieldInfo.scenarios.length
                            && baseFieldInfo.scenarios.map((s, i) => (
                                <div className="base-process" key={i}>
                                    {s?.scenario}
                                </div>
                            ))}
                    </Form.Item>
                )}

                {fields.includes('solution') && (
                    <Form.Item label={`${getFieldName('solution') || '解决方案'}：`}>
                        <div>{baseFieldInfo?.solution}</div>
                    </Form.Item>
                )}

                {fields.includes('benefits') && (
                    <Form.Item label={`${getFieldName('benefits') || '效果效益'}：`}>
                        {baseFieldInfo?.benefits
                            && baseFieldInfo.benefits.length
                            && baseFieldInfo.benefits.map((b, i) => (
                                <div className="base-process" key={i}>
                                    {b?.benefits}
                                </div>
                            ))}
                    </Form.Item>
                )}

                {fields.includes('solved_problems') && (
                    <Form.Item label={`${getFieldName('solved_problems') || '痛点堵点'}：`}>
                        {baseFieldInfo?.solved_problems
                            && baseFieldInfo.solved_problems.length
                            && baseFieldInfo.solved_problems.map((p, i) => (
                                <div className="base-process" key={i}>
                                    {p?.solved_problems}
                                </div>
                            ))}
                    </Form.Item>
                )}

                {fields.includes('honors') && (
                    <Form.Item label={`${getFieldName('honors') || '曾获荣誉'}：`}>
                        {baseFieldInfo?.honors
                            && baseFieldInfo.honors.length
                            && baseFieldInfo.honors.map((h, i) => (
                                <div className="base-process" key={i}>
                                    {h?.honor}
                                </div>
                            ))}
                    </Form.Item>
                )}

                {fields.includes('applied_hospitals') && (
                    <Form.Item label={`${getFieldName('applied_hospitals') || '应用医院'}：`}>
                        {baseFieldInfo?.applied_hospitals
                            && baseFieldInfo.applied_hospitals.length
                            && baseFieldInfo.applied_hospitals.map((h, i) => (
                                <div className="base-process" key={i}>
                                    {h?.name}
                                </div>
                            ))}
                    </Form.Item>
                )}

                {fields.includes('technologies') && (
                    <Form.Item label={`${getFieldName('technologies') || '相关技术'}：`}>
                        {baseFieldInfo?.technologies
                            && baseFieldInfo.technologies.length
                            && baseFieldInfo.technologies.map((t, i) => (
                                <div className="base-process" key={i}>
                                    {t?.technology}
                                </div>
                            ))}
                    </Form.Item>
                )}

                {fields.includes('organizations') && (
                    <Form.Item label={`${getFieldName('organizations') || '合作机构'}：`}>
                        {baseFieldInfo?.organizations
                            && baseFieldInfo.organizations.length
                            && baseFieldInfo.organizations.map((o, i) => (
                                <div className="base-process" key={i}>
                                    {o?.name}
                                </div>
                            ))}
                    </Form.Item>
                )}

                {fields.includes('served_times') && (
                    <Form.Item label={`${getFieldName('served_times') || '服务人次'}：`}>
                        <div>{baseFieldInfo?.served_times}</div>
                    </Form.Item>
                )}

                {fields.includes('period') && (
                    <Form.Item label={`${getFieldName('period') || '实施周期'}：`}>
                        {!!baseFieldInfo?.period?.start_date && (
                            <>
                                {baseFieldInfo.period?.start_date} ～ {!!baseFieldInfo.period?.end_date ? baseFieldInfo.period?.end_date : '至今'}
                            </>
                        )}
                    </Form.Item>
                )}

                {fields.includes('exam_indicators') && (
                    <Form.Item label={`${getFieldName('exam_indicators') || '对应国考指标'}：`}>
                        {baseFieldInfo?.exam_indicators
                            && baseFieldInfo.exam_indicators.length
                            && baseFieldInfo.exam_indicators.map((e, i) => (
                                <div className="base-process" key={i}>
                                    {e?.exam_indicator ? e?.exam_indicator.join(' / ') : ''}
                                </div>
                            ))}
                    </Form.Item>
                )}
            </Form>
        </div>
    );
};

// 实践联系人
const CaseContact = ({ caseFields, getFieldName, getSecondField }) => {
    const params = useParams();
    // 字典数据列表
    const [dicCodeList, setDicCodeList] = useState([]);
    const [fields, setFields] = useState([]);

    const [contactLeader, setContactLeader] = useState([]);
    const [contactDicList, setContactDicList] = useState([]);
    const [hospitalContact, setHospitalContact] = useState([]);
    const [enterpriseContact, setEnterpriseContact] = useState([]);

    const getCaseContactInfo = async caseId => {
        const res = await caseContactInfoApi({ id: caseId });
        if (res.status_code === 200 && res.data && res.data.fieldValue) {
            const field = JSON.parse(res.data.fieldValue);
            if (field.contacts && field.contacts.length) {
                setContactDicList(field.contacts);
            }
            if (field.case_leader_contacts && field.case_leader_contacts.length) {
                setContactLeader(field.case_leader_contacts);
            }
            if (field.hospital_contacts && field.hospital_contacts.length) {
                setHospitalContact(field.hospital_contacts);
            }
            if (field.enterprise_contacts && field.enterprise_contacts.length) {
                setEnterpriseContact(field.enterprise_contacts);
            }
        }
    };

    // 获取字典名称
    const getDicName = dicCode => resolveDicName(dicCodeList, dicCode);

    // 获取职务职称字段
    const getCodeName = async () => {
        const res = await getDicNameApi({ identity: 'A01' });
        if (res.status_code === 200 && res.data) {
            setDicCodeList(res.data);
        }
    };

    useEffect(() => {
        getCodeName();
        getCaseContactInfo(params.id);
    }, []);

    useEffect(() => {
        if (caseFields) {
            const newCaseFields = caseFields.map(item => item.field_name);
            setFields(newCaseFields);
        }
    }, [caseFields]);

    return (
        <div className="contact-info">
            <Form layout="horizontal">
                {fields.includes('contacts') && (
                    <Form.Item label={`${getFieldName('contacts') || '实践主导负责人'}：`}>
                        {dicCodeList.length
                            && contactDicList
                            && contactDicList.length
                            && contactDicList.map((c, i) => (
                                <div className="contact-process" key={i}>
                                    {getSecondField('contacts', 'name') && (
                                        <div>
                                            <span>姓名：</span>
                                            {c.name}
                                        </div>
                                    )}
                                    {getSecondField('contacts', 'department') && (
                                        <div>
                                            <span>科室/部门：</span>
                                            {getDicName(c.department)}
                                        </div>
                                    )}
                                    {getSecondField('contacts', 'post') && (
                                        <div>
                                            <span>职务：</span>
                                            {getDicName(c.post)}
                                        </div>
                                    )}
                                    {getSecondField('contacts', 'phone') && (
                                        <div>
                                            <span>手机：</span>
                                            {c.phone}
                                        </div>
                                    )}
                                    {getSecondField('contacts', 'email') && (
                                        <div>
                                            <span>邮箱：</span>
                                            {c.email}
                                        </div>
                                    )}
                                </div>
                            ))}
                    </Form.Item>
                )}

                {fields.includes('case_leader_contacts') && (
                    <Form.Item label={`${getFieldName('case_leader_contacts') || '实践牵头人'}：`}>
                        {dicCodeList.length
                            && contactLeader
                            && contactLeader.length
                            && contactLeader.map((c, i) => (
                                <div className="contact-process" key={i}>
                                    {getSecondField('case_leader_contacts', 'name') && (
                                        <div>
                                            <span>姓名：</span>
                                            {c.name}
                                        </div>
                                    )}
                                    {getSecondField('case_leader_contacts', 'department') && (
                                        <div>
                                            <span>科室/部门：</span>
                                            {getDicName(c.department)}
                                        </div>
                                    )}
                                    {getSecondField('case_leader_contacts', 'post') && (
                                        <div>
                                            <span>职务：</span>
                                            {getDicName(c.post)}
                                        </div>
                                    )}
                                    {getSecondField('case_leader_contacts', 'phone') && (
                                        <div>
                                            <span>手机：</span>
                                            {c.phone}
                                        </div>
                                    )}
                                    {getSecondField('case_leader_contacts', 'email') && (
                                        <div>
                                            <span>邮箱：</span>
                                            {c.email}
                                        </div>
                                    )}
                                </div>
                            ))}
                    </Form.Item>
                )}

                {fields.includes('hospital_contacts') && (
                    <Form.Item label={`${getFieldName('hospital_contacts') || '医院联系人'}：`}>
                        {dicCodeList.length
                            && hospitalContact
                            && hospitalContact.length
                            && hospitalContact.map((c, i) => (
                                <div className="contact-process" key={i}>
                                    {getSecondField('hospital_contacts', 'name') && (
                                        <div>
                                            <span>姓名：</span>
                                            {c.name}
                                        </div>
                                    )}
                                    {getSecondField('hospital_contacts', 'department') && (
                                        <div>
                                            <span>科室/部门：</span>
                                            {getDicName(c.department)}
                                        </div>
                                    )}
                                    {getSecondField('hospital_contacts', 'post') && (
                                        <div>
                                            <span>职务：</span>
                                            {getDicName(c.post)}
                                        </div>
                                    )}

                                    {getSecondField('hospital_contacts', 'phone') && (
                                        <div>
                                            <span>手机：</span>
                                            {c.phone}
                                        </div>
                                    )}
                                    {getSecondField('hospital_contacts', 'email') && (
                                        <div>
                                            <span>邮箱：</span>
                                            {c.email}
                                        </div>
                                    )}
                                </div>
                            ))}
                    </Form.Item>
                )}

                {fields.includes('enterprise_contacts') && (
                    <Form.Item label={`${getFieldName('enterprise_contacts') || '企业联系人'}：`}>
                        {dicCodeList.length
                            && enterpriseContact
                            && enterpriseContact.length
                            && enterpriseContact.map((c, i) => (
                                <div className="contact-process" key={i}>
                                    {getSecondField('enterprise_contacts', 'name') && (
                                        <div>
                                            <span>姓名：</span>
                                            {c.name}
                                        </div>
                                    )}
                                    {getSecondField('enterprise_contacts', 'department') && (
                                        <div>
                                            <span>科室/部门：</span>
                                            {getDicName(c.department)}
                                        </div>
                                    )}
                                    {getSecondField('enterprise_contacts', 'post') && (
                                        <div>
                                            <span>职务：</span>
                                            {getDicName(c.post)}
                                        </div>
                                    )}
                                    {getSecondField('enterprise_contacts', 'phone') && (
                                        <div>
                                            <span>手机：</span>
                                            {c.phone}
                                        </div>
                                    )}
                                    {getSecondField('enterprise_contacts', 'email') && (
                                        <div>
                                            <span>邮箱：</span>
                                            {c.email}
                                        </div>
                                    )}
                                </div>
                            ))}
                    </Form.Item>
                )}
            </Form>
        </div>
    );
};

// 实践材料
const CaseFile = ({ caseFields, getFieldName }) => {
    const params = useParams();
    const caseId = params.id;

    console.log(caseFields);

    const [caseFile, setCaseFile] = useState({});

    const [fileFields, setFileFields] = useState([]);

    // 上传演示材料的阶段
    const [stages, setStages] = useState([]);

    // 演示材料所在的阶段ID
    const [stageId, setStageId] = useState(0);

    const [visibleVideoPreview, setVisibleVideoPreview] = useState(false);

    const [visibleImagePreview, setVisibleImagePreview] = useState(false);
    const [imagesToPreview, setImagesToPreview] = useState([]);

    const [form] = Form.useForm();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [value, setValue] = useState(1);

    const ReviewStatus = { 0: '待审核', 1: '审核通过', 2: '驳回' };

    useEffect(() => {
        if (caseFields) {
            const newCaseFields = caseFields.map(item => item.field_name);
            setFileFields(newCaseFields);
        }
    }, [caseFields]);

    // 视频预览
    const videoPreview = async mediaId => {
        if (!mediaId) {
            return;
        }
        if (player) {
            player.dispose();
        }

        const playInfo = await getVodMediaInfoApi({ mediaId });
        const resData = playInfo.data;
        if (!!resData && window.Aliplayer) {
            if (resData.status === 'Normal') {
                const playauth = resData.playAuth;
                player = new window.Aliplayer(
                    {
                        id: 'J_prismPlayer',
                        width: '720px',
                        height: '405px',
                        vid: mediaId,
                        playauth,
                        autoplay: false,
                        isLive: false,
                        rePlay: false,
                        playsinline: false,
                        preload: true,
                        skinLayout: playerSkinLayout,
                    },
                    player => {},
                );
            }
        }
    };

    const openVideoPreview = videoId => {
        videoPreview(videoId);
        setVisibleVideoPreview(true);
    };

    const closeVideoPreview = () => {
        //

        setVisibleVideoPreview(false);
    };

    const previewDocument = path => {
        if (/\.pdf$/i.test(path)) {
            window.open(prepareBCOSSUrl(path));
        } else {
            const url = `https://view.officeapps.live.com/op/view.aspx?src=${prepareBCOSSUrl(path)}`;
            window.open(url);
        }
    };

    // 在新窗口打开源文件
    const openOriginalFile = path => window.open(prepareBCOSSUrl(path));

    const previewImages = images => {
        setVisibleImagePreview(true);
        setImagesToPreview(images);
    };

    const renderStageCaseFile = row => {
        if (!isEmpty(row.files)) {
            const files = row.files;
            const { doc, video, images, certificates } = files;

            return (
                <div className="stage-case-file-cell">
                    <div className="attach-list-cell">
                        {!isEmpty(doc) && (
                            <div className="attach-item doc">
                                <span className="attach-icon">
                                    <i className="bi bi-file-earmark"></i>
                                </span>
                                <span>文档</span>
                                <Button type="link" size="small" onClick={() => previewDocument(doc?.path)}>
                                    预览
                                </Button>
                                <Button type="link" size="small" onClick={() => openOriginalFile(doc?.path)}>
                                    下载
                                </Button>
                            </div>
                        )}

                        {!isEmpty(video) && (
                            <div className="attach-item video">
                                <span className="attach-icon">
                                    <i className="bi bi-file-play"></i>
                                </span>
                                <span>视频</span>
                                <Button type="link" size="small" onClick={() => openVideoPreview(video?.videoId)}>
                                    预览
                                </Button>
                                <Button type="link" size="small" onClick={() => openOriginalFile(video?.path)}>
                                    下载
                                </Button>
                            </div>
                        )}

                        {!isEmpty(images) && (
                            <div className="attach-item images">
                                <span className="attach-icon">{images.length > 1 ? <i className="bi bi-images"></i> : <i className="bi bi-image"></i>}</span>
                                <span>图片</span>
                                <Button type="link" size="small" onClick={() => previewImages(images)}>
                                    预览
                                </Button>
                            </div>
                        )}
                    </div>

                    {!isEmpty(certificates) && (
                        <div className="cert-list">
                            <div className="cert-title">证明材料：</div>
                            {certificates.map((item, idx) => (
                                <div className="cert-item" key={`cert_${idx}`}>
                                    <span>{item.title}</span>
                                    <Button type="link" size="small" onClick={() => openOriginalFile(item.url)}>
                                        下载
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        } else {
            return <Text type="secondary">&ndash;</Text>;
        }
    };

    const columns = [
        { title: '阶段', dataIndex: 'stageName', width: 100 },
        {
            title: '实践材料',
            dataIndex: 'filePreview',
            render: (_, row) => renderStageCaseFile(row),
        },
        {
            title: '更新时间',
            dataIndex: 'updatedAt',
            align: 'center',
            width: 200,
            render: text => <Text type="secondary">{dateTimeFormatHMS(text)}</Text>,
        },
        {
            title: '文档展示状态',
            dataIndex: 'showed',
            align: 'center',
            width: 100,
            render: v => (!!v ? <Text type="success">默认展示</Text> : <Text type="secondary">&ndash;</Text>),
        },
        {
            title: '审核状态',
            dataIndex: 'reviewedStatus',
            align: 'center',
            width: 80,
            render: (_, row) => (row.stageName === '申报阶段' ? <Text type="secondary">&ndash;</Text> : ReviewStatus[row.reviewedStatus]),
        },
        {
            title: '操作',
            dataIndex: 'operation',
            width: 200,
            render: (_, row) => {
                const canExamine = row.stageName !== '申报阶段' && (row.reviewedStatus === 0 || row.reviewedStatus === 1);
                const canShow = !row.showed && row.reviewedStatus === 1;
                return (
                    <span className="operation-btns">
                        {canExamine && (
                            <Button type="link" size="small" onClick={() => showReviewModal(row)}>
                                审核
                            </Button>
                        )}
                        {canShow && (
                            <Button type="link" size="small" onClick={() => showChangeAction(row)}>
                                设为默认展示
                            </Button>
                        )}
                    </span>
                );
            },
        },
    ];

    // 视频点播 - 主题配置
    const playerSkinLayout = [
        { name: 'bigPlayButton', align: 'blabs', x: 30, y: 80 },
        { name: 'H5Loading', align: 'cc' },
        { name: 'errorDisplay', align: 'tlabs', x: 0, y: 0 },
        { name: 'infoDisplay' },
        { name: 'tooltip', align: 'blabs', x: 0, y: 56 },
        { name: 'thumbnail' },
        {
            name: 'controlBar',
            align: 'blabs',
            x: 0,
            y: 0,
            children: [
                { name: 'progress', align: 'blabs', x: 0, y: 44 },
                { name: 'playButton', align: 'tl', x: 15, y: 12 },
                { name: 'timeDisplay', align: 'tl', x: 10, y: 7 },
                { name: 'fullScreenButton', align: 'tr', x: 10, y: 12 },
                // {name:"subtitle", align:"tr",x:15, y:12},
                { name: 'setting', align: 'tr', x: 15, y: 12 },
                { name: 'volume', align: 'tr', x: 5, y: 10 },
            ],
        },
    ];

    // 附件预览
    const attachmentPreview = doc => {
        window.open(prepareBCOSSUrl(doc.path));
    };

    // const attachmentRawFile = doc => window.open(prepareBCOSSUrl(doc.path));
    const attachmentRawFile = doc => {
        if (/\.pdf$/i.test(doc.path)) {
            window.open(prepareBCOSSUrl(doc.path));
        } else {
            const officeUrl = `https://view.officeapps.live.com/op/view.aspx?src=${prepareBCOSSUrl(doc.path)}`;
            window.open(officeUrl);
        }
    };

    const showChangeAction = async stage => {
        const param = {
            caseId,
            showed: 1,
            id: stage.stageName === '申报阶段' ? undefined : stage.id,
        };
        const res = await caseFileShowedChangeApi(param);
        if (res.status_code === 200) {
            if (res.data === 1 || res.data === 3) {
                message.error(res.message);
            } else {
                message.success('展示状态设置成功');

                getAllStageList();
            }
        }
    };

    const showReviewModal = stageWithAttachmentFiles => {
        setStageId(stageWithAttachmentFiles.id);
        setIsModalOpen(true);
    };

    const submitReview = () => {
        form.validateFields().then(async values => {
            const data = {
                id: stageId,
                reviewedStatus: values.status,
                ...values,
            };
            const res = await caseFileReviewedApi(data);
            if (res.status_code === 200) {
                message.success('审核完成');
                getAllStageList();
                setIsModalOpen(false);
            }
        });
    };

    const cancelReview = () => {
        setIsModalOpen(false);
    };

    const changeReviewStatus = e => {
        setValue(e.target.value);
    };

    const getAllStageList = () => {
        // 申报阶段
        let startStage = null;
        const p1 = caseFileApiApi({ id: caseId }).then(res => {
            if (res.status_code === 200) {
                startStage = res.data; // 申报阶段 Object
                if (!!startStage.fieldValue) {
                    // 统一数据结构
                    startStage['files'] = JSON.parse(startStage.fieldValue) || null;
                }

                // 统一数据结构
                // 操作中会用到这个标识：<code>stage.stageName === '申报阶段' ? undefined : stage.id</code>
                startStage['stageName'] = '申报阶段';
            }
        });

        // 其他阶段
        let otherStageList = [];
        const p2 = caseOtherFileApi({ caseId }).then(res => {
            if (res.status_code === 200) {
                const stageList = res.data; // 其他阶段列表 Array<Object>
                if (stageList && stageList.length) {
                    otherStageList = stageList.map(s => {
                        if (!!s.files) {
                            s.files = JSON.parse(s.files) || null;

                            if (s.files.video && s.playInfos && s.playInfos.length) {
                                s.files.video.path = s.playInfos[0]['playUrl'];
                            }
                        }

                        return s;
                    });
                }
            }
        });

        Promise.all([p1, p2]).then(() => {
            // 确定哪一个阶段的实践材料默认展示
            let visible = false;
            otherStageList.forEach(s => {
                if (s.showed) {
                    visible = true;
                }
            });
            if (!visible) {
                startStage['showed'] = true; // 统一数据结构
            }

            // 整合申报阶段和其他阶段的数据
            const compactStages = [...[startStage], ...otherStageList];
            setStages(compactStages);
            console.log(compactStages);
        });
    };

    useEffect(() => {
        if (caseId) {
            getAllStageList(caseId);
        }
    }, [caseId]);

    return (
        <>
            <div className="base-info">
                <Table size="small" columns={columns} dataSource={stages} pagination={false} />
            </div>

            <div style={{ display: 'none' }}>
                {!!imagesToPreview.length && (
                    <Image.PreviewGroup
                        preview={{
                            visible: visibleImagePreview,
                            onVisibleChange: v => setVisibleImagePreview(v),
                        }}
                    >
                        {imagesToPreview.map((img, i) => (
                            <Image src={prepareBCOSSUrl(img.url)} key={`img_${i}`} alt={`实践图片${i + 1}`} />
                        ))}
                    </Image.PreviewGroup>
                )}
            </div>

            <Modal title="视频预览" open={visibleVideoPreview} width={768} centered footer={null} onCancel={() => closeVideoPreview()}>
                <div id="J_prismPlayer" style={{ width: 720, height: 405, backgroundColor: 'black' }} />
            </Modal>

            <Modal title="审核实践材料" open={isModalOpen} onOk={submitReview} onCancel={cancelReview}>
                <Form name="auditForm" form={form} labelCol={{ span: 4 }} wrapperCol={{ span: 20 }} autoComplete="off">
                    <Form.Item label="审核意见" name="status" rules={[{ required: true }]}>
                        <Radio.Group onChange={changeReviewStatus} value={value}>
                            <Radio value={1}>通过</Radio>
                            <Radio value={2}>驳回</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {value === 2 ? (
                        <Form.Item name="rejectReason" label="驳回理由" rules={[{ required: true, message: '请输入驳回理由' }]}>
                            <TextArea rows={4} placeholder="输入驳回理由" showCount maxLength={200} />
                        </Form.Item>
                    ) : null}
                </Form>
            </Modal>
        </>
    );
};

/** 参与人 memberInfo 认证展示：统一空位（HTML 实体 &ndash;） */
const renderAuthSecondaryDash = () => <Text type="secondary">&ndash;</Text>;

const parseMemIdentityFromPost = memPost => {
    if (memPost == null || typeof memPost !== 'string') {
        return null;
    }
    const first = memPost.split('-')[0];
    return /^A\d{2}$/.test(first) ? first : null;
};

const getPersonalIdentityLabelForCaseTeam = code => {
    const item = identityTypes.find(t => t.code === code);
    return item?.label ?? code;
};

const resolveMemIdentityCode = (memberInfo, row) => {
    if (!memberInfo) {
        return null;
    }
    if (memberInfo.memIdentity) {
        return memberInfo.memIdentity;
    }
    return parseMemIdentityFromPost(memberInfo.memPost) || parseMemIdentityFromPost(row?.memPost) || null;
};

const getMemberAuthIdentityLabel = (memberInfo, row) => {
    if (!memberInfo) {
        return renderAuthSecondaryDash();
    }
    const mas = Number(memberInfo.memAuthStatus);
    if (mas === 0) {
        return renderAuthSecondaryDash();
    }
    const mt = Number(memberInfo.memType);
    if (mt === 1) {
        const code = resolveMemIdentityCode(memberInfo, row);
        if (!code) {
            return renderAuthSecondaryDash();
        }
        return getPersonalIdentityLabelForCaseTeam(code);
    }
    if (mt === 2) {
        const ot = memberInfo.orgType;
        const n = ot === '' || ot == null ? NaN : Number(ot);
        if (Number.isNaN(n)) {
            return renderAuthSecondaryDash();
        }
        const orgItem = orgAuthTypes.find(item => item.orgAuthType === n);
        return orgItem?.label != null ? orgItem.label : renderAuthSecondaryDash();
    }
    return renderAuthSecondaryDash();
};

const getMemberAuthStatusLabel = memberInfo => {
    if (!memberInfo) {
        return renderAuthSecondaryDash();
    }
    const mas = Number(memberInfo.memAuthStatus);
    if (mas === 0) {
        return '未认证';
    }
    const mt = Number(memberInfo.memType);
    if (mt === 1) {
        const my = Number(memberInfo.memIsyijiehui);
        if (my === 3) {
            return '认证审核中';
        }
        if (my === 1) {
            return '认证审核通过';
        }
        if (my === 4) {
            return '认证审核驳回';
        }
        return renderAuthSecondaryDash();
    }
    if (mt === 2) {
        if (mas === 1) {
            return '认证审核中';
        }
        if (mas === 2) {
            return '认证审核通过';
        }
        if (mas === 3) {
            return '认证审核驳回';
        }
        return renderAuthSecondaryDash();
    }
    return renderAuthSecondaryDash();
};

// 实践参赛团队
const CaseTeam = ({ caseId, caseFields, memcard }) => {
    const params = useParams();
    const [data, setData] = useState([]);

    const [stageData, setStageData] = useState([]);
    const [contactData, setContactData] = useState([]);
    const [personData, setPersonData] = useState([]);

    const columns = [
        { title: '姓名', dataIndex: 'username', key: 'username' },
        { title: '科室部门', dataIndex: 'memOfficeText', key: 'memOffice', default: '—' },
        { title: '职务', dataIndex: 'memPostText', key: 'memPost', default: '—' },
        { title: '手机号', dataIndex: 'mobile', key: 'mobile' },
        { title: '邮箱', dataIndex: 'email', key: 'email', default: '—' },
        { title: '阶段', dataIndex: 'stageName', key: 'stageName', default: '—' },
        { title: '角色', dataIndex: 'memTask', key: 'memTask', default: '—' },
        {
            title: '认证信息',
            key: 'memberAuth',
            render: (_, record) => {
                if (!record.memberInfo) {
                    return renderAuthSecondaryDash();
                }
                const statusLabel = getMemberAuthStatusLabel(record.memberInfo);
                const statusNode = isValidElement(statusLabel) ? statusLabel : statusLabel === '认证审核通过' ? <Text type="success">{statusLabel}</Text> : statusLabel === '认证审核驳回' ? <Text type="warning">{statusLabel}</Text> : statusLabel;
                return (
                    <div>
                        <div>{getMemberAuthIdentityLabel(record.memberInfo, record)}</div>
                        <div>{statusNode}</div>
                    </div>
                );
            },
        },
        {
            title: '确认状态',
            dataIndex: 'confirmStatus',
            key: 'confirmStatus',
            render: confirmStatus => {
                if (confirmStatus === 0 || confirmStatus === '0') {
                    return '未确认';
                }
                if (confirmStatus === 1 || confirmStatus === '1') {
                    return <Text type="success">已确认</Text>;
                }
                return renderAuthSecondaryDash();
            },
        },
    ];

    const sortData = () => {
        const combinedData = [...contactData, ...personData, ...stageData].map(item => ({
            ...item,
            key: `${item.mobile || ''}_${item.stageName || ''}`,
        }));
        setData(combinedData);
    };

    // 获取字典名称
    const getDicName = dicCode => resolveDicName(allDicCodeList, dicCode);

    // 获取参与人（团队成员）
    const getCaseTeam = async () => {
        const res = await caseTeamApi({ caseId: params.id });
        if (res.status_code === 200 && res.data) {
            setStageData(res.data);
        }
    };

    const getCaseContactInfo = async caseId => {
        const res = await caseContactInfoApi({ id: caseId });
        if (res.status_code === 200 && res.data && res.data.fieldValue) {
            const field = JSON.parse(res.data.fieldValue);
            if (field.contacts && field.contacts.length) {
                var tmpD = field.contacts.map(item => {
                    var newItem = {
                        ...item,
                        stageName: '全部阶段',
                        memTask: '实践负责人',
                        memOfficeText: getDicName(item.department),
                        memPostText: getDicName(item.post),
                        memJobtitleText: getDicName(item.title),
                        mobile: item.phone,
                        username: item.name,
                    };
                    return newItem;
                });
                setContactData(tmpD);
            }
        }
    };

    const getDeclarationInfo = async memcard => {
        const res = await getPeopleInfoApi({ memCard: memcard });
        if (res.data) {
            setPersonData([
                {
                    ...res.data,
                    stageName: '申报阶段',
                    memTask: '申报人、实践负责人',
                    memOfficeText: res.data.qitaMap?.memOffice,
                    memPostText: res.data.qitaMap?.memPost,
                    memJobtitleText: getDicName([res.data.qitaMap?.memJobtitleCode]),
                    mobile: res.data.memMobile,
                    username: res.data.memNickname,
                },
            ]);
        }
    };

    useEffect(() => {
        sortData();
    }, [stageData, personData, contactData]);

    useEffect(() => {
        getDeclarationInfo(memcard);
        getCaseContactInfo(caseId);
        getCaseTeam();
    }, []);

    return (
        <div className="base-info">
            <Table size="small" columns={columns} dataSource={data} pagination={false} />
        </div>
    );
};

// 实践详情
const CaseDetail = () => {
    const navigate = useNavigate();
    const params = useParams();
    const caseId = parseInt(params.id) || 0;
    const adminPhone = useMemo(() => String(getAdminInfo().adminPhone || '').trim(), []);
    const { modal } = App.useApp();

    const [form] = Form.useForm();
    const [configForm] = Form.useForm();

    const [caseInfo, setCaseInfo] = useState({});
    const [fieldInfo, setFieldInfo] = useState({});
    const [caseFields, setCaseFields] = useState(null);
    /** 实践所属项目（用于判断是否展示 AI 初审结果） */
    const [projectInfo, setProjectInfo] = useState(null);

    const [visibleCaseReviewModal, setVisibleCaseReviewModal] = useState(false);
    const [configModalOpen, setConfigModalOpen] = useState(false);
    /** 未配置截稿/企微时的引导，点击「立即配置」打开「驳回理由配置」 */
    const [auditConfigNudgeModalOpen, setAuditConfigNudgeModalOpen] = useState(false);

    const [reviewing, setReviewing] = useState(false); // 审核中 loading 状态

    /** 本地配置：截稿、企微、自定义理由（与 case 所属 projectId 分 key 存于 localStorage） */
    const [auditSettings, setAuditSettings] = useState(() => loadSettings(undefined));
    /** 已勾选的预置理由 id */
    const [selectedPresetIds, setSelectedPresetIds] = useState([]);
    /** 预置的页码、主题等附加输入 */
    const [presetExtras, setPresetExtras] = useState({});
    /** 已选中的 my_reasons 下标（升序） */
    const [selectedMyCustom, setSelectedMyCustom] = useState([]);
    /** 已勾选的 AI 初审理由 key（page_num / template / duplicate） */
    const [selectedAiKeys, setSelectedAiKeys] = useState([]);
    /** 右栏临时驳回理由（不落配置，实时拼入左栏生成全文） */
    const [tempRejectReason, setTempRejectReason] = useState('');
    /** 左栏驳回理由是否与最近一次「右栏生成」全文不一致（曾手改） */
    const [rejectReasonManuallyEdited, setRejectReasonManuallyEdited] = useState(false);
    const lastAutoRejectReasonRef = useRef('');

    const auditFormStatus = Form.useWatch('status', form);
    const rejectReasonWatch = Form.useWatch('rejectReason', form);

    const caseProjectId = caseInfo?.projectId;

    useEffect(() => {
        setAuditSettings(loadSettings(caseProjectId));
    }, [caseProjectId]);

    // 字段名称
    const getFieldName = name => caseFields.find(item => item.field_name === name)?.field_display_name;

    // 二级字段内容是否展示
    const getSecondField = (name, secondName) => {
        const fieldItem = caseFields.find(item => item.field_name === name);
        if (fieldItem?.children && fieldItem.children.length > 0) {
            const isShow = fieldItem.children.some(item => item.field_name === secondName && item.checked);
            return isShow;
        }
        return false;
    };

    // 渲染子组件
    const getChildComponent = type => {
        // '基础信息', '实践联系人', '实践材料', '参赛团队'
        switch (type) {
            case 0:
                return <BaseInfo caseId={params.id} caseFields={caseFields} getFieldName={getFieldName} getSecondField={getSecondField} />;
            case 1:
                return <CasePoster fieldInfo={fieldInfo} caseInfo={caseInfo} />;
            case 2:
                return <CaseContact caseId={params.id} caseFields={caseFields} getFieldName={getFieldName} getSecondField={getSecondField} />;
            case 3:
                return <CaseFile caseId={params.id} caseFields={caseFields} caseInfo={caseInfo} getFieldName={getFieldName} />;
            case 4:
                return <CaseTeam caseId={params.id} caseFields={caseFields} getFieldName={getFieldName} memcard={caseInfo.memcard} />;
        }
    };

    // 审核状态（理由在状态行下方成对卡片中展示，此处不再挂 Tooltip/图标）
    const getAuditStatus = caseInfo => {
        if (caseInfo.reviewedStatus === 0 && caseInfo.status === 0) {
            return <Text type="secondary">&ndash;</Text>;
        } else if (caseInfo.reviewedStatus === 0 && caseInfo.status > 0) {
            return <span>待审核</span>;
        } else if (caseInfo.reviewedStatus === 1) {
            return <Text type="success">已通过</Text>;
        } else if (caseInfo.reviewedStatus === 2) {
            return <Text type="warning">已驳回</Text>;
        }
    };

    // 发布状态
    const getReleaseStatus = status => {
        if (status === 0) {
            return '草稿';
        }
        if (status === 1) {
            return '未发布';
        }
        if (status === 2) {
            return '已上架';
        }
        if (status === 3) {
            return '已下架';
        }
    };

    // 获取实践详情
    const getCaseDetail = async caseId => {
        const res = await caseDetailApi({ id: caseId });
        if (res.status_code === 200 && res.data) {
            setCaseInfo(res.data);
            if (res.data.fieldValue) {
                setFieldInfo(JSON.parse(res.data.fieldValue));
            }
            getProjectTopicFields(res.data.topicId);
        }
    };

    // 获取实践分配字段
    const getProjectTopicFields = async topicId => {
        const res = await getProjectTopicFieldsApi({ topicId });
        if (res?.status_code === 200 && res.data) {
            let fields = [];
            try {
                fields = JSON.parse(res.data?.caseFields);
            } catch (e) {}
            if (!!fields) {
                setCaseFields(fields);
            }
        }
    };

    const openCaseReviewModal = () => {
        const nextSettings = loadSettings(caseProjectId);
        setAuditSettings(nextSettings);
        const aiParts = parseAiReviewMessageParts(caseInfo?.aiReviewMsg);
        setSelectedAiKeys(AI_REVIEW_MSG_KEYS.filter(k => aiParts[k]));
        const aiAbnormal = projectInfo && isProjectAiReviewEnabled(projectInfo) && Number(caseInfo?.aiReviewStat) === 3;
        if (aiAbnormal) {
            form.setFieldsValue({ status: 2 });
        }
        setVisibleCaseReviewModal(true);
        if (aiAbnormal && !isDeadlineReady(nextSettings)) {
            queueMicrotask(() => {
                setAuditConfigNudgeModalOpen(true);
            });
        }
    };

    const closeCaseReviewModal = () => {
        form.resetFields();
        setSelectedPresetIds([]);
        setPresetExtras({});
        setSelectedMyCustom([]);
        setSelectedAiKeys([]);
        setTempRejectReason('');
        lastAutoRejectReasonRef.current = '';
        setRejectReasonManuallyEdited(false);
        setAuditConfigNudgeModalOpen(false);
        setVisibleCaseReviewModal(false);
    };

    const openConfigModal = () => {
        const s = loadSettings(caseProjectId);
        configForm.setFieldsValue({
            dead_line: s.dead_line,
            wework: s.wework,
            impl_years_enabled: s.impl_years_enabled === true,
            min_impl_years: s.min_impl_years ?? '',
            my_reasons: s.my_reasons?.length ? s.my_reasons : [],
        });
        setConfigModalOpen(true);
    };

    const openAuditConfigNudgeModal = () => {
        setAuditConfigNudgeModalOpen(true);
    };

    const handleAuditConfigNudgeImmediate = () => {
        setAuditConfigNudgeModalOpen(false);
        openConfigModal();
    };

    const handleConfigOk = () => {
        configForm.validateFields().then(values => {
            const myReasons = (values.my_reasons || []).map(x => (typeof x === 'string' ? x : String(x || '')).trim()).filter(Boolean);
            const implOn = values.impl_years_enabled === true;
            const next = {
                dead_line: (values.dead_line || '').trim(),
                wework: (values.wework || '').trim(),
                impl_years_enabled: implOn,
                min_impl_years: implOn ? (values.min_impl_years == null ? '' : String(values.min_impl_years)).trim() : '',
                my_reasons: myReasons,
            };
            saveSettings(next, caseProjectId);
            setAuditSettings(next);
            setConfigModalOpen(false);
            message.success('配置已保存');
        });
    };

    const clearAuditConfig = () => {
        const next = { ...DEFAULT_SETTINGS, my_reasons: [] };
        saveSettings(next, caseProjectId);
        setAuditSettings(next);
        configForm.setFieldsValue({
            dead_line: next.dead_line,
            wework: next.wework,
            impl_years_enabled: next.impl_years_enabled,
            min_impl_years: next.min_impl_years,
            my_reasons: next.my_reasons,
        });
        configForm.validateFields().catch(() => {});
        message.success('配置已清空');
    };

    const fillWeworkWithAdminPhone = () => {
        if (!adminPhone) {
            message.warning('未获取到登录手机号');
            return;
        }
        configForm.setFieldValue('wework', adminPhone);
        message.success('已填入登录手机号');
    };

    // 选「通过」时清空驳回理由
    useEffect(() => {
        if (auditFormStatus === 1) {
            form.setFieldValue('rejectReason', undefined);
            lastAutoRejectReasonRef.current = '';
            setTempRejectReason('');
            setRejectReasonManuallyEdited(false);
        }
    }, [auditFormStatus, form]);

    const aiReviewPartsForCase = useMemo(() => parseAiReviewMessageParts(caseInfo?.aiReviewMsg), [caseInfo?.aiReviewMsg]);

    const aiRejectSegments = useMemo(() => AI_REVIEW_MSG_KEYS.filter(k => selectedAiKeys.includes(k) && aiReviewPartsForCase[k]).map(k => aiReviewPartsForCase[k]), [selectedAiKeys, aiReviewPartsForCase]);

    /** 未开启「实践实施周期年限」时不展示对应预置项 */
    const visiblePresetReasons = useMemo(() => PRESET_REASONS.filter(p => p.id !== 'impl_years' || auditSettings.impl_years_enabled), [auditSettings.impl_years_enabled]);

    useEffect(() => {
        if (!auditSettings.impl_years_enabled) {
            setSelectedPresetIds(prev => (prev.includes('impl_years') ? prev.filter(id => id !== 'impl_years') : prev));
        }
    }, [auditSettings.impl_years_enabled]);

    // 选择理由变化时重新生成「驳回理由」全文
    useEffect(() => {
        if (auditFormStatus !== 2) {
            return;
        }
        const customTexts = selectedMyCustom.map(i => auditSettings.my_reasons[i]).filter(t => t && String(t).trim());
        const text = buildRejectLetter({
            settings: {
                dead_line: auditSettings.dead_line,
                wework: auditSettings.wework,
                impl_years_enabled: auditSettings.impl_years_enabled,
                min_impl_years: auditSettings.min_impl_years,
            },
            selectedPresetIds,
            presetExtras,
            selectedCustomReasons: customTexts,
            aiReviewSegments: aiRejectSegments,
            tempReason: tempRejectReason,
        });
        lastAutoRejectReasonRef.current = text;
        form.setFieldValue('rejectReason', text);
        setRejectReasonManuallyEdited(false);
    }, [auditFormStatus, auditSettings, selectedPresetIds, presetExtras, selectedMyCustom, aiRejectSegments, tempRejectReason, form]);

    // 配置里删减自定义项后，去掉无效下标
    useEffect(() => {
        setSelectedMyCustom(prev => prev.filter(i => i >= 0 && i < (auditSettings.my_reasons?.length || 0)));
    }, [auditSettings.my_reasons]);

    // 提交审核
    const submitCaseReview = () => {
        form.validateFields()
            .then(async values => {
                if (values.status === 2) {
                    if (!isDeadlineReady(auditSettings)) {
                        setAuditConfigNudgeModalOpen(true);
                        return;
                    }
                    if (!hasRejectReasonSelection(selectedPresetIds, selectedMyCustom) && aiRejectSegments.length === 0 && !String(tempRejectReason).trim() && !(rejectReasonManuallyEdited && String(values.rejectReason ?? '').trim())) {
                        message.error('请至少选择一条驳回理由、填写临时驳回理由或手动修改驳回理由');
                        return;
                    }
                    const ve = validatePresetExtras(selectedPresetIds, presetExtras, auditSettings);
                    if (!ve.ok) {
                        message.error(ve.message);
                        return;
                    }
                }
                setReviewing(true);
                try {
                    const data = {
                        id: caseId,
                        status: values.status,
                        rejectReason: values.status === 2 ? values.rejectReason || '' : '',
                    };
                    const res = await caseAuditApi(data);
                    if (res?.status_code === 200) {
                        message.success('实践审核意见提交成功');
                        setVisibleCaseReviewModal(false);
                        setAuditConfigNudgeModalOpen(false);
                        getCaseDetail(params.id);
                        form.resetFields();
                        setSelectedPresetIds([]);
                        setPresetExtras({});
                        setSelectedMyCustom([]);
                        setSelectedAiKeys([]);
                        setTempRejectReason('');
                        lastAutoRejectReasonRef.current = '';
                        setRejectReasonManuallyEdited(false);
                    }
                } finally {
                    setReviewing(false);
                }
            })
            .catch(() => {});
    };

    useEffect(() => {
        getCaseDetail(params.id);
    }, []);

    useEffect(() => {
        const pid = caseInfo?.projectId;
        if (pid == null) {
            setProjectInfo(null);
            return;
        }
        let cancelled = false;
        getProjestsApi({ id: pid })
            .then(res => {
                if (cancelled) {
                    return;
                }
                if (res?.status_code === 200 && res.data) {
                    setProjectInfo(res.data);
                } else {
                    setProjectInfo(null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setProjectInfo(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [caseInfo?.projectId]);

    const aiReviewStat = caseInfo.aiReviewStat; // AI审核状态
    const aiReviewMsgRaw = caseInfo.aiReviewMsg; // AI审核结果
    const aiRejectReasonText = formatAiReviewRejectReasons(aiReviewMsgRaw); // AI审核驳回理由

    const rejectReasonRaw = String(caseInfo.rejectReason ?? '').trim();
    const showAiReasonCard = !!(projectInfo && isProjectAiReviewEnabled(projectInfo) && Number(aiReviewStat) === 3 && aiRejectReasonText);
    const isRejectedWithReason = caseInfo.reviewedStatus === 2 && !!rejectReasonRaw;
    // 待审核但保留上次驳回理由（用户修改后重新提交）
    const isPendingWithPrevReject = caseInfo.reviewedStatus === 0 && caseInfo.status > 0 && !!rejectReasonRaw;
    // 草稿但保留上次驳回理由（驳回后用户撤回修改）
    const isDraftWithPrevReject = caseInfo.status === 0 && !!rejectReasonRaw;
    const hasPrevRejectReason = isPendingWithPrevReject || isDraftWithPrevReject;
    const showRejectReasonCard = isRejectedWithReason || hasPrevRejectReason;

    // 渲染AI审核状态（异常理由在状态行下方成对卡片中展示）
    const renderAiReviewStatus = () => {
        const stat = Number(aiReviewStat);
        if (stat === 0) {
            return <span>待审核</span>;
        }
        if (stat === 1) {
            return <span>审核中</span>;
        }
        if (stat === 2) {
            return <Text type="success">已通过</Text>;
        }
        if (stat === 3) {
            return <Text type="warning">存在异常</Text>;
        }
        return <Text type="secondary">&ndash;</Text>;
    };

    /** 预置理由：page 已勾选时文案后填页码；theme 已勾选时行内投报主题；kind 为 years 时年数取项目「审核驳回配置」 */
    const renderAuditPresetLabel = (p, checked) => {
        if (p.kind === 'years' && p.suffix) {
            const y = String(auditSettings?.min_impl_years ?? '').trim();
            return (
                <span className="reject-preset-label">
                    {p.text}
                    {y || '（未配置）'}
                    {p.suffix}
                </span>
            );
        }
        if (p.kind === 'theme') {
            if (!checked) {
                return <span className="reject-preset-label">{p.text}</span>;
            }
            return (
                <span className="reject-preset-label reject-preset-label--theme-inline">
                    {p.text}
                    <Input
                        className="reject-preset-theme-input"
                        size="small"
                        placeholder="建议投报主题（选填）"
                        value={presetExtras[p.id]?.theme ?? ''}
                        onChange={e =>
                            setPresetExtras(ex => ({
                                ...ex,
                                [p.id]: { ...ex[p.id], theme: e.target.value },
                            }))
                        }
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                    />
                </span>
            );
        }
        if (p.kind !== 'page') {
            return <span className="reject-preset-label">{p.text}</span>;
        }
        if (!checked) {
            return <span className="reject-preset-label">{p.text}</span>;
        }
        return (
            <span className="reject-preset-label reject-preset-label--page-inline">
                {p.text}
                <Input
                    className="reject-preset-page-input"
                    size="small"
                    placeholder="页码（选填）"
                    value={presetExtras[p.id]?.page ?? ''}
                    onChange={e =>
                        setPresetExtras(ex => ({
                            ...ex,
                            [p.id]: { ...ex[p.id], page: e.target.value },
                        }))
                    }
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                />
            </span>
        );
    };

    const auditConfigReady = isDeadlineReady(auditSettings);
    const hasManualRejectReason = rejectReasonManuallyEdited && !!String(rejectReasonWatch ?? '').trim();
    const hasReasonPicked = hasRejectReasonSelection(selectedPresetIds, selectedMyCustom) || aiRejectSegments.length > 0 || !!String(tempRejectReason).trim() || hasManualRejectReason;
    const rejectPresetExtrasOk = validatePresetExtras(selectedPresetIds, presetExtras, auditSettings).ok;
    const warnAuditConfigRequired = () => {
        openAuditConfigNudgeModal();
    };
    /** 已手改左栏驳回理由时，调整右栏勾选前二次确认（确认后由生成 effect 覆盖全文） */
    const confirmOverwriteManualRejectReason = onConfirm => {
        if (!rejectReasonManuallyEdited) {
            onConfirm();
            return;
        }
        modal.confirm({
            title: '提示',
            content: '您已手动修改驳回理由，调整右栏可选驳回理由会覆盖驳回理由！',
            okText: '知晓并确定',
            cancelText: '取消',
            centered: true,
            onOk: () => {
                onConfirm();
            },
        });
    };
    const auditModalOkDisabled = reviewing || (auditFormStatus === 2 && (!auditConfigReady || !hasReasonPicked || !rejectPresetExtrasOk));

    const showAuditOkDisabledHint = () => {
        if (reviewing) {
            return;
        }
        if (auditFormStatus !== 2) {
            return;
        }
        if (!auditConfigReady) {
            openAuditConfigNudgeModal();
            return;
        }
        if (!hasReasonPicked) {
            message.warning({ content: '请至少选择一条驳回理由、填写临时驳回理由或手动修改驳回理由', key: 'audit-modal-ok-hint' });
            return;
        }
        if (!rejectPresetExtrasOk) {
            const ve = validatePresetExtras(selectedPresetIds, presetExtras, auditSettings);
            message.warning({ content: ve.ok ? '请完善已勾选理由的必填项' : ve.message, key: 'audit-modal-ok-hint' });
        }
    };

    const caseAuditConfigScopeText = useMemo(() => {
        const n = projectInfo?.name != null && String(projectInfo.name).trim();
        if (n) {
            return (
                <>
                    以下配置项针对本项目（{n}）。
                    <br />
                    配置保存在本地浏览器缓存中，更换浏览器或电脑将需要重新配置。
                </>
            );
        }
        return '以下配置项按征集项目分别保存，仅作用于对应该项目的实践。';
    }, [projectInfo, caseProjectId]);

    return (
        <>
            <PageHeader
                title={
                    <>
                        <i className="bi bi-chevron-left page-header-back-btn" onClick={() => navigate('/selection/cases')} />
                        <span>实践详情</span>
                    </>
                }
                breadcrumb={[{ label: '医评议' }, { label: '实践管理', path: '/selection/cases' }, '实践详情']}
            />

            <div className="page-route-body case-detail-page">
                <div className="case-detail">
                    <h2>{fieldInfo?.title}</h2>
                    <p className="detail-header">
                        <span>提交时间：{caseInfo.createdAt}</span>

                        {projectInfo && isProjectAiReviewEnabled(projectInfo) && <span>AI初审结果：{renderAiReviewStatus()}</span>}

                        <span>审核状态：{getAuditStatus(caseInfo)}</span>

                        {!!caseInfo.reviewedStatus && (
                            <>
                                <span>审核时间：{caseInfo.reviewedAt}</span>
                                <span>审核人：{caseInfo.reviewedName}</span>
                            </>
                        )}

                        <span>发布状态：{getReleaseStatus(caseInfo.status)}</span>
                    </p>

                    {showAiReasonCard || showRejectReasonCard ? (
                        <div className="case-reason-cards">
                            {showAiReasonCard ? (
                                <div className="case-reason-card case-reason-card--ai">
                                    <div className="case-reason-card-title">
                                        AI初审结果：<Text type="warning">存在异常</Text>
                                    </div>
                                    <ReasonExpandableText key={`ai-${aiRejectReasonText}`} text={aiRejectReasonText} />
                                </div>
                            ) : null}

                            {showRejectReasonCard ? (
                                <div className="case-reason-card case-reason-card--reject">
                                    <div className="case-reason-card-title">
                                        {hasPrevRejectReason ? (
                                            <>
                                                上次驳回理由
                                                {(caseInfo.reviewedName || caseInfo.reviewedAt) && (
                                                    <Text type="secondary" className="case-reason-card-meta">
                                                        （{caseInfo.reviewedName ? `审核人：${caseInfo.reviewedName}` : null}
                                                        {caseInfo.reviewedName && caseInfo.reviewedAt ? '，' : null}
                                                        {caseInfo.reviewedAt ? `审核时间：${caseInfo.reviewedAt}` : null}）
                                                    </Text>
                                                )}
                                            </>
                                        ) : (
                                            <>审核状态：{isRejectedWithReason ? <Text type="warning">已驳回</Text> : <span>待审核</span>}</>
                                        )}
                                    </div>
                                    <ReasonExpandableText key={`reject-${rejectReasonRaw}-${hasPrevRejectReason ? 'prev' : 'cur'}`} text={rejectReasonRaw} />
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {caseFields && <Tabs type="card" items={['基础信息', '成果壁报', '实践联系人', '实践材料', '参与人员'].map((title, i) => ({ label: title, key: i, children: getChildComponent(i) }))} />}

                    {caseInfo?.status > 0 && (
                        <div className="page-form-foot-operations">
                            <Space size="large">
                                <Button type="primary" size="large" onClick={openCaseReviewModal}>
                                    审核实践
                                </Button>
                            </Space>
                        </div>
                    )}
                </div>
            </div>

            <Modal
                title="审核实践"
                width={auditFormStatus === 2 ? 1000 : 600}
                className="rejectReasonModal"
                open={visibleCaseReviewModal}
                onCancel={closeCaseReviewModal}
                centered
                maskClosable={false}
                footer={
                    <div className="reject-reason-modal-footer">
                        {auditFormStatus === 2 ? <span className="reject-reason-modal-footer-hint">左栏为最终提交驳回理由；右栏勾选与配置会生成并覆盖左栏内容。</span> : <span className="reject-reason-modal-footer-hint" />}
                        <div className="reject-reason-modal-footer-actions">
                            <Space>
                                <Button onClick={closeCaseReviewModal}>取消</Button>
                                <div className="reject-reason-ok-button-wrap">
                                    <Button type="primary" loading={reviewing} disabled={auditModalOkDisabled} onClick={submitCaseReview}>
                                        确定
                                    </Button>
                                    {auditModalOkDisabled && !reviewing ? (
                                        <div
                                            className="reject-reason-ok-button-blocker"
                                            role="presentation"
                                            onClick={e => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                showAuditOkDisabledHint();
                                            }}
                                        />
                                    ) : null}
                                </div>
                            </Space>
                        </div>
                    </div>
                }
            >
                {projectInfo && isProjectAiReviewEnabled(projectInfo) && (
                    <Alert
                        type="warning"
                        showIcon={false}
                        style={{ marginBottom: 16, padding: 8 }}
                        description={
                            <>
                                <div style={{ marginBottom: aiRejectReasonText ? 8 : 0 }}>
                                    <b>AI初审结果：{renderAiReviewStatus()}</b>
                                </div>
                                {aiRejectReasonText ? aiRejectReasonText : null}
                            </>
                        }
                    />
                )}

                <Form name="auditForm" form={form} autoComplete="off" layout="vertical">
                    <Form.Item label="审核意见" name="status" rules={[{ required: true, message: '请选择审核意见' }]}>
                        <Radio.Group
                            onChange={e => {
                                if (e.target.value === 2) {
                                    const s = loadSettings(caseProjectId);
                                    if (!isDeadlineReady(s)) {
                                        queueMicrotask(() => {
                                            setAuditConfigNudgeModalOpen(true);
                                        });
                                    }
                                }
                            }}
                        >
                            <Radio value={1}>通过</Radio>
                            <Radio value={2}>驳回</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                            const isReject = getFieldValue('status') === 2;
                            const configLocked = isReject && !auditConfigReady;
                            return (
                                <div className={isReject ? 'audit-reject-transition audit-reject-transition--open' : 'audit-reject-transition'} aria-hidden={!isReject}>
                                    <div className="audit-reject-transition__inner">
                                        <Form.Item
                                            label={
                                                <span className="reject-reason-form-label">
                                                    <span className="reject-reason-form-label-title">驳回理由</span>
                                                    {rejectReasonManuallyEdited ? <span className="reject-reason-label-manual-hint">（已手动修改驳回理由，右栏可选驳回理由的操作会覆盖手动修改内容）</span> : null}
                                                </span>
                                            }
                                            required={isReject}
                                        >
                                            <Row gutter={16} align="top">
                                                <Col xs={24} md={10}>
                                                    <Form.Item name="rejectReason" noStyle rules={isReject ? [{ required: true, message: '请输入或生成驳回理由' }] : []}>
                                                        <TextArea
                                                            rows={7}
                                                            placeholder="与右栏、配置共同生成，可手改"
                                                            showCount
                                                            maxLength={500}
                                                            readOnly={configLocked}
                                                            onMouseDown={e => {
                                                                if (!configLocked) {
                                                                    return;
                                                                }
                                                                e.preventDefault();
                                                                warnAuditConfigRequired();
                                                            }}
                                                            onFocus={e => {
                                                                if (!configLocked) {
                                                                    return;
                                                                }
                                                                e.target.blur();
                                                                warnAuditConfigRequired();
                                                            }}
                                                            onChange={e => {
                                                                const v = e.target.value;
                                                                setRejectReasonManuallyEdited(v !== lastAutoRejectReasonRef.current);
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} md={14}>
                                                    <div className="reject-reason-pick">
                                                        <div className="reject-reason-pick-header">
                                                            <span className="reject-reason-pick-title">可选驳回理由</span>
                                                            <div className="reject-reason-config-float-wrap">
                                                                <Tooltip title={!auditConfigReady && isReject ? '请先完成驳回理由配置' : undefined}>
                                                                    <span className="reject-reason-config-btn-wrap">
                                                                        <Button type="link" size="small" icon={<i className="bi bi-gear" />} onClick={openConfigModal}>
                                                                            驳回理由配置
                                                                        </Button>
                                                                    </span>
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="reject-reason-pick-body"
                                                            onClickCapture={e => {
                                                                if (!configLocked) {
                                                                    return;
                                                                }
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                warnAuditConfigRequired();
                                                            }}
                                                        >
                                                            {projectInfo && isProjectAiReviewEnabled(projectInfo) && hasAiReviewMessageContent(caseInfo?.aiReviewMsg) && (
                                                                <>
                                                                    {AI_REVIEW_MSG_KEYS.map(key => {
                                                                        const line = aiReviewPartsForCase[key];
                                                                        if (!line) {
                                                                            return null;
                                                                        }
                                                                        return (
                                                                            <div key={`ai-review-${key}`} className="reject-preset-item">
                                                                                <Checkbox
                                                                                    checked={selectedAiKeys.includes(key)}
                                                                                    onChange={e => {
                                                                                        const on = e.target.checked;
                                                                                        confirmOverwriteManualRejectReason(() => {
                                                                                            setSelectedAiKeys(prev => {
                                                                                                if (on) {
                                                                                                    return prev.includes(key) ? prev : [...prev, key];
                                                                                                }
                                                                                                return prev.filter(x => x !== key);
                                                                                            });
                                                                                        });
                                                                                    }}
                                                                                >
                                                                                    <span className="reject-preset-label reject-preset-label--ai">
                                                                                        <Tag bordered={false} className="reject-ai-tag">
                                                                                            AI初审
                                                                                        </Tag>
                                                                                        <span>{line}</span>
                                                                                    </span>
                                                                                </Checkbox>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </>
                                                            )}

                                                            {visiblePresetReasons.map(p => {
                                                                const checked = selectedPresetIds.includes(p.id);
                                                                return (
                                                                    <div key={p.id} className="reject-preset-item">
                                                                        <Checkbox
                                                                            checked={checked}
                                                                            onChange={e => {
                                                                                const on = e.target.checked;
                                                                                confirmOverwriteManualRejectReason(() => {
                                                                                    setSelectedPresetIds(prev => {
                                                                                        if (on) {
                                                                                            return prev.includes(p.id) ? prev : [...prev, p.id];
                                                                                        }
                                                                                        return prev.filter(x => x !== p.id);
                                                                                    });
                                                                                    if (!on) {
                                                                                        setPresetExtras(ex => {
                                                                                            const n = { ...ex };
                                                                                            delete n[p.id];
                                                                                            return n;
                                                                                        });
                                                                                    }
                                                                                });
                                                                            }}
                                                                        >
                                                                            {renderAuditPresetLabel(p, checked)}
                                                                        </Checkbox>
                                                                    </div>
                                                                );
                                                            })}

                                                            {!!auditSettings.my_reasons.length && <div className="reject-reason-pick-group">自定义驳回理由</div>}
                                                            {auditSettings.my_reasons.map((text, i) => (
                                                                <div key={`${caseId}-my-reason-${i}-${String(text).slice(0, 32)}`} className="reject-preset-item">
                                                                    <Checkbox
                                                                        checked={selectedMyCustom.includes(i)}
                                                                        onChange={e => {
                                                                            const on = e.target.checked;
                                                                            confirmOverwriteManualRejectReason(() => {
                                                                                setSelectedMyCustom(prev => {
                                                                                    if (on) {
                                                                                        return [...new Set([...prev, i])].sort((a, b) => a - b);
                                                                                    }
                                                                                    return prev.filter(x => x !== i);
                                                                                });
                                                                            });
                                                                        }}
                                                                    >
                                                                        <span className="reject-preset-label">{text}</span>
                                                                    </Checkbox>
                                                                </div>
                                                            ))}

                                                            <div className="reject-reason-pick-group">临时驳回理由</div>
                                                            <TextArea
                                                                className="reject-temp-reason-input"
                                                                rows={2}
                                                                value={tempRejectReason}
                                                                placeholder="输入临时驳回理由，将实时拼入左侧驳回理由"
                                                                maxLength={200}
                                                                showCount
                                                                onChange={e => setTempRejectReason(e.target.value)}
                                                                onClick={e => e.stopPropagation()}
                                                                onMouseDown={e => e.stopPropagation()}
                                                            />
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Form.Item>
                                    </div>
                                </div>
                            );
                        }}
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="请先完成驳回理由配置"
                className="auditConfigNudgeModal"
                open={auditConfigNudgeModalOpen}
                onCancel={() => setAuditConfigNudgeModalOpen(false)}
                zIndex={1200}
                centered
                width={480}
                destroyOnClose
                maskClosable
                footer={
                    <Space>
                        <Button onClick={() => setAuditConfigNudgeModalOpen(false)}>稍后</Button>
                        <Button type="primary" onClick={handleAuditConfigNudgeImmediate}>
                            立即配置
                        </Button>
                    </Space>
                }
            >
                <p className="audit-config-nudge-text">
                    请先完成<em>修改截止时间</em>、联系人<em>企微号</em>、<em>实践实施周期年限</em>等配置项，用于驳回理由模板内容的替换。
                </p>
            </Modal>

            <Modal
                title="驳回理由配置"
                className="caseAuditConfigModal"
                width={600}
                zIndex={1100}
                open={configModalOpen}
                centered
                onCancel={() => setConfigModalOpen(false)}
                destroyOnHidden
                footer={
                    <div className="case-audit-config-footer">
                        <Button danger onClick={clearAuditConfig}>
                            清空配置
                        </Button>
                        <Space>
                            <Button onClick={() => setConfigModalOpen(false)}>取消</Button>
                            <Button type="primary" onClick={handleConfigOk}>
                                确定
                            </Button>
                        </Space>
                    </div>
                }
            >
                <Alert type="warning" className="case-audit-config-scope-alert" message={caseAuditConfigScopeText} />

                <Form form={configForm} layout="vertical" autoComplete="off" preserve={false}>
                    <Form.Item name="dead_line" label="修改截止时间" extra="明确告知申报人驳回实践的修改截止时间，将填入驳回理由“请于…前”中" rules={[{ required: true, message: '请输入修改截止时间' }]}>
                        <Input placeholder="如 8月4日18点" allowClear />
                    </Form.Item>

                    <Form.Item
                        name="wework"
                        label={
                            <>
                                <span>企微号</span>（
                                <Button type="link" size="small" onClick={fillWeworkWithAdminPhone} style={{ padding: 0 }}>
                                    使用我的登录手机号
                                </Button>
                                ）
                            </>
                        }
                        extra="填写实践审核小组组长企微号或个人企微号，以便申报人收到驳回信息后及时联系"
                        rules={[{ required: true, message: '请输入企微号' }]}
                    >
                        <Input placeholder="组委会工作人员企微号" allowClear />
                    </Form.Item>

                    <Form.Item name="impl_years_enabled" label="实践实施周期年限" valuePropName="checked" extra="开启后须填写起算年限，可选理由中将展示“实践启动实施距今未满 N 年”">
                        <Switch />
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.impl_years_enabled !== cur.impl_years_enabled}>
                        {({ getFieldValue }) =>
                            getFieldValue('impl_years_enabled') ? (
                                <Form.Item name="min_impl_years" label="起算年限" rules={[{ required: true, message: '请输入起算年限' }]}>
                                    <Input placeholder="填写阿拉伯数字，如 2 表示满 2 年" allowClear />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>

                    <div className="case-audit-my-reasons-block">
                        <div className="case-audit-my-reasons-title">自定义驳回理由</div>
                        <Form.List name="my_reasons">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(field => (
                                        <div key={field.key} className="case-audit-my-reason-row">
                                            <div className="case-audit-my-reason-input-wrap">
                                                <Form.Item name={field.name} noStyle>
                                                    <Input placeholder="请输入自定义理由" />
                                                </Form.Item>
                                            </div>
                                            <Button type="link" danger size="small" className="case-audit-my-reason-delete" onClick={() => remove(field.name)}>
                                                删除
                                            </Button>
                                        </div>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add('')} block icon={<i className="bi bi-plus-circle"></i>}>
                                            新增
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                    </div>
                </Form>
            </Modal>
        </>
    );
};

export default CaseDetail;
